/**
 * №22.5 — the DecisionCard's derivation rules, as pure functions.
 *
 * A MIRROR of `src/engine/collaboration.ts` (the console cannot import the worker's
 * source; the whole `lib/types.ts` file is a mirror for the same reason). Mirrors rot,
 * so this one is GATED: `scripts/test-card-trace-22.5.ts` imports both this module and
 * the engine's originals and asserts they agree, fixture by fixture. If a rule changes
 * on one side only, the gate fails — the divergence cannot ship quietly, which is what
 * "gate the class, not the instance" means for a duplicated rule.
 *
 * Everything here is a rule about WHAT THE CARD IS, not about how it looks, which is
 * why it lives beside the card rather than inside it: the same question ("is this card
 * pre-filled, so does it need a Confirm?") is asked by the renderer, by the tests, and
 * — in the mirror's original — by the server.
 */
import type { NeedsYouOption } from "../../../lib/types";

/**
 * `select` — the card carries a PRE-FILLED option (C3), so it renders as radio rows
 *   with an explicit Confirm. The commit is a separate, deliberate act: a pre-fill is
 *   the record answering, not the human, and it must never confirm itself.
 * `direct` — nothing is pre-selected, so each option button IS the commit, exactly as
 *   D-CC2 shipped it. One human act either way; unchanged for every existing card.
 */
export type DecisionCardMode = "select" | "direct";

/** The index of the pre-filled option, or null. A pre-fill without a stated source is
 *  not a pre-fill — the server strips it, and this agrees so a stale payload can't
 *  produce a selected option with nothing to explain it. */
export function prefilledIndex(options: readonly NeedsYouOption[] | null | undefined): number | null {
  if (!Array.isArray(options)) return null;
  const i = options.findIndex((o) => o?.prefilled === true && typeof o.prefilled_from === "string" && o.prefilled_from.trim() !== "");
  return i >= 0 ? i : null;
}

export function decisionCardMode(options: readonly NeedsYouOption[] | null | undefined): DecisionCardMode {
  const opts = Array.isArray(options) ? options : [];
  if (opts.length === 0) return "direct";

  /**
   * №53 · E-28 (David, 2026-08-19) — THE COMMIT GRAMMAR.
   *
   * This used to read: a pre-fill anywhere ⇒ select-then-Confirm (§22.5 C3). The
   * crew drawing commits on the option click and has no Confirm at all, and both
   * could not be right. David's ruling: *keep Confirm where the option touches
   * money or an outbound that changes a term; let everything else commit on the
   * click.*
   *
   * **The money half was already impossible.** `sanitizeNeedsYouOptions` has
   * dropped money verbs from card options outright since D-CC8, so no card can
   * carry one. What remains is the outbound — and the reason it keeps the second
   * press is not that it matters more but that it is IRREVERSIBLE: a task can be
   * reopened and a draft rewritten; an email that reached the other side's
   * solicitor is a fact about the world.
   *
   * The classification is the SERVER's (`commitGrammarFor`, stamped onto the
   * stored option at raise time), never this file's — a console deployed ahead of
   * its worker must not decide what needs a second press using an older rule.
   *
   * **Absence means UNSTAMPED, and an unstamped card is answered by the OLD rule.**
   * That distinction is the whole of the fallback and the first cut got it wrong:
   * an option with no `commit` is not a card that ASKED for a second press, it is
   * one raised before the field existed — by an older worker, a fixture, or any
   * path that does not run the sanitizer. Reading those as `confirm` turns every
   * one-click card in the product into a two-step.
   *
   * Where the stamp IS present, a pre-fill no longer forces the two-step by
   * itself. C3's real requirement — that a pre-filled answer is legible as a
   * PROPOSAL before it becomes an act — is then carried by the recommended row's
   * tint and its provenance line, which now render in direct mode too. That is a
   * deliberate change to C3, not an accident of one.
   */
  const stamped = opts.some((o) => o.commit === "confirm" || o.commit === "click");
  if (!stamped) return prefilledIndex(opts) != null ? "select" : "direct";
  return opts.some((o) => o.commit === "confirm") ? "select" : "direct";
}

/** "‹ 2 of 4 ›" — null below two questions: a pager over one is chrome claiming a queue. */
export function pagerLabel(index: number, total: number): string | null {
  if (!Number.isFinite(total) || total < 2) return null;
  const at = Math.min(Math.max(Number.isFinite(index) ? Math.floor(index) : 0, 0), total - 1);
  return `‹ ${at + 1} of ${total} ›`;
}

/**
 * "3 more after this". The locked mockup reads "3 more after this · run continues
 * meanwhile" and the second clause is deliberately NOT rendered: in Kuvio a raised
 * card is a terminal run ending, so the run is stopped and the member of staff is
 * paused — the card says exactly that a line above. See the findings.
 */
export function remainderLine(index: number, total: number): string | null {
  if (!Number.isFinite(total) || total < 1) return null;
  const at = Math.min(Math.max(Number.isFinite(index) ? Math.floor(index) : 0, 0), total - 1);
  const n = Math.max(0, total - 1 - at);
  return n > 0 ? `${n} more after this` : null;
}

/** What skipping records — the authored line, else the generic true one (C4). */
export function skipLine(authored: string | null | undefined, anchorLabel?: string | null): string {
  const a = (authored ?? "").trim();
  if (a) return a;
  const where = (anchorLabel ?? "").trim();
  return where ? `Skipping leaves this question unresolved on ${where}.` : "Skipping leaves this question unresolved.";
}

/**
 * The evidence chip's link. `ref` is an opaque id whose TYPE the card does not know,
 * so only refs whose route can be built with certainty become links — task ids today.
 * Everything else renders its ref as a visible provenance tail instead of a link that
 * would 404. A named source with no link beats a link that goes nowhere (T2's rule,
 * applied to evidence).
 */
export function evidenceHref(ref: string | null | undefined): string | undefined {
  const r = (ref ?? "").trim();
  if (!r) return undefined;
  if (/^tsk_[A-Za-z0-9_-]+$/.test(r)) return `/app/task/${r}`;
  return undefined;
}
